import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getWeeklyWalkthrough,
  isWeeklyWalkthroughId,
  WEEKLY_WALKTHROUGH_EVENTS,
  type WeeklyWalkthrough,
  type WeeklyWalkthroughId,
} from "../lib/weekly-walkthroughs";
import type { SurfaceNavTab } from "../lib/surface-capabilities";

interface UseWeeklyWalkthroughsInput {
  navigateTab: (tab: SurfaceNavTab) => void;
}

export function useWeeklyWalkthroughs({
  navigateTab,
}: UseWeeklyWalkthroughsInput) {
  const [activeId, setActiveId] = useState<WeeklyWalkthroughId | null>(null);
  const hasConsumedUrl = useRef(false);

  const dispatchTarget = useCallback((walkthrough: WeeklyWalkthrough) => {
    window.setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent(walkthrough.eventName, {
          detail: walkthrough.eventDetail ?? {},
        }),
      );
    }, walkthrough.dispatchDelayMs ?? 180);
  }, []);

  const openWalkthrough = useCallback(
    (id: WeeklyWalkthroughId) => {
      const walkthrough = getWeeklyWalkthrough(id);
      if (!walkthrough) return;
      setActiveId(id);
      navigateTab(walkthrough.targetTab);
      dispatchTarget(walkthrough);
    },
    [dispatchTarget, navigateTab],
  );

  useEffect(() => {
    const handler = (event: Event) => {
      const id = (event as CustomEvent<{ id?: unknown }>).detail?.id;
      if (isWeeklyWalkthroughId(id)) openWalkthrough(id);
    };
    window.addEventListener(WEEKLY_WALKTHROUGH_EVENTS.open, handler);
    return () =>
      window.removeEventListener(WEEKLY_WALKTHROUGH_EVENTS.open, handler);
  }, [openWalkthrough]);

  useEffect(() => {
    if (hasConsumedUrl.current || typeof window === "undefined") return;
    hasConsumedUrl.current = true;
    const params = new URLSearchParams(window.location.search);
    const id =
      params.get("weeklyWalkthrough") ??
      params.get("walkthrough") ??
      params.get("demo");
    if (isWeeklyWalkthroughId(id)) openWalkthrough(id);
  }, [openWalkthrough]);

  const activeWalkthrough = useMemo(
    () => getWeeklyWalkthrough(activeId),
    [activeId],
  );

  const replayWalkthrough = useCallback(() => {
    if (activeId) openWalkthrough(activeId);
  }, [activeId, openWalkthrough]);

  return {
    activeWalkthrough,
    closeWalkthrough: () => setActiveId(null),
    replayWalkthrough,
  };
}
